import type {
  CreateGroupTalkSessionParams,
  GroupTalkIntakeResponse,
  GroupTalkMessageDto,
  GroupTalkParticipantDto,
  GroupTalkSessionResponse,
  GroupTalkSessionStateDto,
  GroupTalkTurnResponse,
} from "./api";
import {
  findGroupTalkMember,
  findGroupTalkStanceLabel,
  type GroupTalkMemberId,
  type GroupTalkParticipant,
  type GroupTalkStanceId,
  type GroupTalkTemplateId,
} from "./groupTalk";

export type GroupTalkSessionStateView = {
  currentCategory: string;
  currentFocusPoint: string;
  openQuestions: string[];
  decisionStatus: string;
};

export type GroupTalkSessionMemberView = {
  participantId: string;
  memberId: GroupTalkMemberId;
  speakerId: string;
  label: string;
  role: "facilitator" | "expert";
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  facilitator: boolean;
};

export type GroupTalkSessionView = {
  sessionId: string;
  templateId: string | null;
  title: string;
  description: string;
  members: GroupTalkSessionMemberView[];
  agreedNotes: string[];
  sessionState: GroupTalkSessionStateView;
  status: string;
  createdAt: string;
};

export type GroupTalkTranscriptMessage = {
  messageId: string;
  participantId: string | null;
  memberId: GroupTalkMemberId | null;
  speakerId: string;
  speakerName: string;
  speakerType: "user" | "facilitator" | "expert";
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  facilitator: boolean;
  phase: "user" | "thinking" | "intake" | "expert" | "summary";
  sequence: number | null;
  content: string;
  createdAt: string;
};

export type GroupTalkTurnView = {
  sessionId: string;
  turnId: string;
  messages: GroupTalkTranscriptMessage[];
  createdAt: string;
  isSatisfied: boolean;
};

export type GroupTalkIntakeView = {
  sessionId: string;
  turnId: string;
  messages: GroupTalkTranscriptMessage[];
  createdAt: string;
  agreedNotes: string[];
  sessionState: GroupTalkSessionStateView;
  needsFollowUp: boolean;
  followUpQuestion: string;
  continueToken: string | null;
};

function normalizeAgreedNotes(value: GroupTalkSessionResponse["agreed_notes"] | GroupTalkTurnResponse["agreed_notes"]) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value
      .split("\n")
      .map((item) => item.replace(/^[-*]\s*/, "").trim())
      .filter((item) => item.length > 0);
  }
  return [];
}

function normalizeSessionState(value: GroupTalkSessionStateDto | null | undefined): GroupTalkSessionStateView {
  const rawOpenQuestions = value?.open_questions;
  const openQuestions = Array.isArray(rawOpenQuestions)
    ? rawOpenQuestions.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];

  return {
    currentCategory: typeof value?.current_category === "string" ? value.current_category : "",
    currentFocusPoint: typeof value?.current_focus_point === "string" ? value.current_focus_point : "",
    openQuestions,
    decisionStatus: typeof value?.decision_status === "string" ? value.decision_status : "discussing",
  };
}

function normalizeParticipantStance(stance: string | null | undefined): GroupTalkStanceId | null {
  if (
    stance === "bold" ||
    stance === "careful" ||
    stance === "idea" ||
    stance === "realist" ||
    stance === "numbers" ||
    stance === "user_first" ||
    stance === "skeptic" ||
    stance === "contrarian"
  ) {
    return stance;
  }
  return null;
}

function normalizeParticipantMemberId(memberId: string | null | undefined): GroupTalkMemberId {
  if (
    memberId === "mei" ||
    memberId === "cmo" ||
    memberId === "cto" ||
    memberId === "cfo" ||
    memberId === "coo" ||
    memberId === "pm" ||
    memberId === "tax" ||
    memberId === "law" ||
    memberId === "labor" ||
    memberId === "customer" ||
    memberId === "consultant"
  ) {
    return memberId;
  }
  return "consultant";
}

function normalizeParticipantView(
  participant: GroupTalkParticipantDto,
  facilitatorName: string
): GroupTalkSessionMemberView {
  const memberId = normalizeParticipantMemberId(participant.member_id);
  const member = findGroupTalkMember(memberId);
  const stanceId = normalizeParticipantStance(participant.stance);
  const stanceLabel = findGroupTalkStanceLabel(stanceId);
  const facilitator = participant.role === "facilitator";

  return {
    participantId: participant.participant_id,
    memberId,
    speakerId: participant.speaker_id,
    label:
      typeof participant.display_name === "string" && participant.display_name.trim().length > 0
        ? participant.display_name
        : facilitator
          ? facilitatorName
          : stanceLabel
            ? `${member.label}（${stanceLabel}）`
            : member.label,
    role: participant.role,
    roleLabel: member.roleLabel,
    stanceId,
    stanceLabel,
    facilitator,
  };
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripMessageNoise(raw: string, options: { speakerId: string; speakerName: string }) {
  const normalized = raw.replace(/\r\n/g, "\n").trim();
  if (!normalized) return "";

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .filter(
      (line) =>
        !/^speaker_id\s*:/i.test(line) &&
        !/^member_id\s*:/i.test(line) &&
        !/^speaker_name\s*:/i.test(line) &&
        !/^phase\s*:/i.test(line) &&
        !/^sequence\s*:/i.test(line)
    );

  let joined = lines.join("\n").trim();
  if (!joined) return "";

  const candidates = [options.speakerId, options.speakerName].filter((value) => value.trim().length > 0);

  for (const candidate of candidates) {
    joined = joined.replace(new RegExp(`^${escapeRegExp(candidate)}\\s*[:：]\\s*`, "i"), "").trim();
  }

  return joined;
}

function normalizeMessagePhase(
  message: GroupTalkMessageDto,
  facilitatorSeenCount: number
): GroupTalkTranscriptMessage["phase"] {
  if (message.speaker_type === "user") return "user";
  if (message.phase === "intake" || message.phase === "expert" || message.phase === "summary") {
    return message.phase;
  }
  if (message.speaker_type === "expert") {
    return "expert";
  }
  return facilitatorSeenCount === 0 ? "intake" : "summary";
}

function normalizeMessageView(
  message: GroupTalkMessageDto,
  phase: GroupTalkTranscriptMessage["phase"],
  createdAt: string,
  membersByParticipantId: Map<string, GroupTalkSessionMemberView>,
  facilitatorName: string
): GroupTalkTranscriptMessage {
  const member =
    (message.participant_id ? membersByParticipantId.get(message.participant_id) : undefined) ??
    (message.member_id
      ? normalizeParticipantView(
          {
            participant_id: message.participant_id ?? `participant_${message.member_id}`,
            member_id: message.member_id,
            speaker_id: message.speaker_id,
            display_name: message.speaker_name,
            role: message.speaker_type === "facilitator" ? "facilitator" : "expert",
            stance: null,
          },
          facilitatorName
        )
      : undefined);

  const speakerName =
    typeof message.speaker_name === "string" && message.speaker_name.trim().length > 0
      ? message.speaker_name
      : member?.label ?? "参加メンバー";

  return {
    messageId: message.message_id,
    participantId: message.participant_id ?? null,
    memberId: message.member_id ? normalizeParticipantMemberId(message.member_id) : null,
    speakerId: message.speaker_id,
    speakerName,
    speakerType: message.speaker_type,
    roleLabel:
      message.speaker_type === "user"
        ? "ユーザー"
        : member?.roleLabel ?? (message.speaker_type === "facilitator" ? "ファシリテータ" : "参加メンバー"),
    stanceId: member?.stanceId ?? null,
    stanceLabel: member?.stanceLabel ?? null,
    facilitator: message.speaker_type === "facilitator",
    phase,
    sequence: typeof message.sequence === "number" ? message.sequence : null,
    content: stripMessageNoise(message.content, {
      speakerId: message.speaker_id,
      speakerName,
    }),
    createdAt,
  };
}

export function normalizeGroupTalkSessionView(
  response: GroupTalkSessionResponse,
  facilitatorName: string
): GroupTalkSessionView {
  const members = response.members.map((member) => normalizeParticipantView(member, facilitatorName));

  return {
    sessionId: response.session_id,
    templateId: typeof response.template_id === "string" ? response.template_id : null,
    title: response.title,
    description: typeof response.description === "string" ? response.description : "",
    members,
    agreedNotes: normalizeAgreedNotes(response.agreed_notes),
    sessionState: normalizeSessionState(response.session_state),
    status: typeof response.status === "string" ? response.status : "active",
    createdAt: response.created_at,
  };
}

export function normalizeGroupTalkIntakeView(
  response: GroupTalkIntakeResponse,
  session: GroupTalkSessionView,
  facilitatorName: string
): GroupTalkIntakeView {
  const membersByParticipantId = new Map(session.members.map((member) => [member.participantId, member]));
  let facilitatorSeenCount = 0;

  return {
    sessionId: response.session_id,
    turnId: response.turn_id,
    messages: response.messages.map((message) => {
      const phase = normalizeMessagePhase(message, facilitatorSeenCount);
      if (message.speaker_type === "facilitator") {
        facilitatorSeenCount += 1;
      }
      return normalizeMessageView(message, phase, response.created_at, membersByParticipantId, facilitatorName);
    }),
    createdAt: response.created_at,
    agreedNotes: normalizeAgreedNotes(response.agreed_notes),
    sessionState: normalizeSessionState(response.session_state),
    needsFollowUp: Boolean(response.needs_follow_up),
    followUpQuestion:
      typeof response.follow_up_question === "string" ? response.follow_up_question.trim() : "",
    continueToken:
      typeof response.continue_token === "string" && response.continue_token.trim().length > 0
        ? response.continue_token
        : null,
  };
}

export function normalizeGroupTalkTurnView(
  response: GroupTalkTurnResponse,
  session: GroupTalkSessionView,
  facilitatorName: string
): GroupTalkTurnView {
  const membersByParticipantId = new Map(session.members.map((member) => [member.participantId, member]));
  let facilitatorSeenCount = 0;

  return {
    sessionId: response.session_id,
    turnId: response.turn_id,
    messages: response.messages.map((message) => {
      const phase = normalizeMessagePhase(message, facilitatorSeenCount);
      if (message.speaker_type === "facilitator") {
        facilitatorSeenCount += 1;
      }
      return normalizeMessageView(message, phase, response.created_at, membersByParticipantId, facilitatorName);
    }),
    createdAt: response.created_at,
    isSatisfied: Boolean(response.is_satisfied),
  };
}

function buildSpeakerId(memberId: GroupTalkMemberId, index: number) {
  if (memberId === "mei") return "mei";
  return `${memberId}${index}`;
}

export function buildCreateGroupTalkSessionParams(params: {
  draft: {
    templateId: GroupTalkTemplateId;
    title: string;
    description: string;
    participants: GroupTalkParticipant[];
  };
}): CreateGroupTalkSessionParams {
  const counters: Partial<Record<GroupTalkMemberId, number>> = {};

  return {
    templateId: params.draft.templateId,
    title: params.draft.title,
    description: params.draft.description,
    members: params.draft.participants.map((participant) => {
      const nextCount = (counters[participant.memberId] ?? 0) + 1;
      counters[participant.memberId] = nextCount;

      return {
        participantId: participant.participantId,
        memberId: participant.memberId,
        speakerId: buildSpeakerId(participant.memberId, nextCount),
        role: participant.memberId === "mei" ? "facilitator" : "expert",
        stance: participant.memberId === "mei" ? null : participant.stanceId,
      };
    }),
  };
}
